import { useState, useEffect } from "react";
import api from "@/lib/api";
import { DollarSign, Users, Scissors, ShoppingCart, TrendingUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const formatPrice = (p) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(p);
const formatDate = (d) => new Date(d).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/stats").then(r => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-[#E60000] border-t-transparent rounded-full animate-spin" /></div>;
  if (!stats) return <div className="p-8 text-gray-400">Gagal memuat data</div>;

  const cards = [
    { icon: DollarSign, label: "Pendapatan Hari Ini", value: formatPrice(stats.today_revenue), color: "text-green-400" },
    { icon: ShoppingCart, label: "Transaksi Hari Ini", value: stats.today_transactions, color: "text-blue-400" },
    { icon: TrendingUp, label: "Pendapatan Bulan Ini", value: formatPrice(stats.month_revenue), color: "text-yellow-400" },
    { icon: Users, label: "Total Pelanggan", value: stats.total_customers, color: "text-purple-400" },
    { icon: Users, label: "Total Karyawan", value: stats.total_employees, color: "text-cyan-400" },
    { icon: Scissors, label: "Layanan Aktif", value: stats.total_services, color: "text-[#E60000]" },
  ];

  return (
    <div className="p-6 md:p-8" data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-white">Dashboard</h1>
        <p className="text-gray-400 text-sm mt-1">Ringkasan operasional FI Barbershop Sekip</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="stats-grid">
        {cards.map((c, i) => (
          <div key={i} className="bg-[#0F0F0F] border border-[#262626] p-5 hover:border-[#333] transition-colors" data-testid={`stat-card-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-500">{c.label}</span>
              <c.icon size={18} className={c.color} />
            </div>
            <p className={`font-heading text-2xl font-bold ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>

      {/* Recent Transactions */}
      <div className="bg-[#0F0F0F] border border-[#262626]" data-testid="recent-transactions">
        <div className="px-5 py-4 border-b border-[#262626]">
          <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-white">Transaksi Terbaru</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#262626]">
                <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Kode</th>
                <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Pelanggan</th>
                <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Total</th>
                <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Metode</th>
                <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Waktu</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent_transactions.length === 0 ? (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-gray-500">Belum ada transaksi</td></tr>
              ) : (
                stats.recent_transactions.map((tx) => (
                  <tr key={tx.id} className="border-b border-[#262626]/50 hover:bg-[#1A1A1A] transition-colors">
                    <td className="px-5 py-3 font-mono text-xs text-[#E60000]">{tx.transaction_code}</td>
                    <td className="px-5 py-3 text-white">{tx.customer_name}</td>
                    <td className="px-5 py-3 text-green-400 font-semibold">{formatPrice(tx.total)}</td>
                    <td className="px-5 py-3"><Badge variant="secondary" className="uppercase text-[10px]">{tx.payment_method}</Badge></td>
                    <td className="px-5 py-3 text-gray-400">{formatDate(tx.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
